import { ProjectDto } from 'types/generated/graphql';

type FranchiseProject = Pick<
  ProjectDto,
  'id' | 'name' | 'ownerSubjectId' | 'ownerCompanyName' | 'providerSubjectId'
>;

export type FranchiseBranch = {
  projectId: string;
  name: string;
};

export type Franchise = {
  ownerSubjectId: string;
  ownerCompanyName: string;
  branches: FranchiseBranch[];
};

const compareCs = (a: string, b: string) =>
  a.localeCompare(b, 'cs', { sensitivity: 'base' });

/**
 * A franchise is not an entity of the platform — it is derived from projects
 * (ADM-40, LUG-008): every owner company of a project whose provider is the
 * given provider subject, except the provider itself. Projects of that owner
 * are its branches. A company without such a project is not a franchise.
 */
export const deriveFranchises = (
  projects: ReadonlyArray<FranchiseProject | null | undefined>,
  providerSubjectId: string | undefined
): Franchise[] => {
  if (!providerSubjectId) {
    return [];
  }

  const byOwner = new Map<string, Franchise>();

  projects.forEach((project) => {
    if (
      !project ||
      project.providerSubjectId !== providerSubjectId ||
      project.ownerSubjectId === providerSubjectId
    ) {
      return;
    }

    const franchise = byOwner.get(project.ownerSubjectId) ?? {
      ownerSubjectId: project.ownerSubjectId,
      ownerCompanyName: project.ownerCompanyName ?? project.ownerSubjectId,
      branches: [],
    };

    franchise.branches.push({ projectId: project.id, name: project.name });
    byOwner.set(project.ownerSubjectId, franchise);
  });

  return Array.from(byOwner.values())
    .map((franchise) => ({
      ...franchise,
      branches: [...franchise.branches].sort((a, b) =>
        compareCs(a.name, b.name)
      ),
    }))
    .sort((a, b) => compareCs(a.ownerCompanyName, b.ownerCompanyName));
};
